'use client';

import { useState } from 'react';
import { cn } from '@/lib/utils';
import {
  X,
  Banknote,
  CreditCard,
  Building2,
  CheckCircle,
  Printer,
} from 'lucide-react';
import { AmountPad } from './NumberPad';
import type { Cart, PaymentMethod, Payment } from '../types';
import { PAYMENT_METHOD_LABELS } from '../types';
import { formatCurrency } from '../../../types';

interface PaymentModalProps {
  cart: Cart;
  onClose: () => void;
  onComplete: (payments: Payment[]) => void;
}

export function PaymentModal({ cart, onClose, onComplete }: PaymentModalProps) {
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('cash');
  const [inputAmount, setInputAmount] = useState(cart.total);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);

  const change = inputAmount - cart.total;
  const isValid = inputAmount >= cart.total;

  const handlePayment = async () => {
    if (!isValid) return;

    setIsProcessing(true);

    // 결제 처리 시뮬레이션
    await new Promise((resolve) => setTimeout(resolve, 1500));

    setIsProcessing(false);
    setIsCompleted(true);
  };

  const handleComplete = () => {
    const payment: Payment = {
      method: paymentMethod,
      amount: inputAmount,
      reference: paymentMethod === 'card' ? `AP${Date.now()}` : undefined,
    };
    onComplete([payment]);
  };

  // 결제 완료 화면
  if (isCompleted) {
    return (
      <ModalWrapper onClose={onClose}>
        <div className="flex flex-col items-center justify-center py-8">
          <div className="w-20 h-20 rounded-full bg-green-500/10 flex items-center justify-center mb-4">
            <CheckCircle className="w-10 h-10 text-green-500" />
          </div>
          <h2 className="text-2xl font-bold mb-2">결제 완료</h2>
          <p className="text-muted-foreground mb-6">결제가 성공적으로 처리되었습니다.</p>

          <div className="w-full max-w-sm space-y-3 mb-6">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">결제 금액</span>
              <span className="font-semibold">{formatCurrency(cart.total)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">결제 수단</span>
              <span>{PAYMENT_METHOD_LABELS[paymentMethod]}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">받은 금액</span>
              <span>{formatCurrency(inputAmount)}</span>
            </div>
            {change > 0 && (
              <div className="flex justify-between text-lg font-bold text-primary pt-2 border-t border-border">
                <span>거스름돈</span>
                <span>{formatCurrency(change)}</span>
              </div>
            )}
          </div>

          <div className="flex gap-3 w-full max-w-sm">
            <button
              onClick={() => alert('영수증 인쇄 (데모)')}
              className="flex-1 flex items-center justify-center gap-2 py-3 rounded-lg border border-border hover:bg-accent"
            >
              <Printer className="w-4 h-4" />
              영수증 인쇄
            </button>
            <button
              onClick={handleComplete}
              className="flex-1 py-3 rounded-lg bg-primary text-primary-foreground font-semibold hover:bg-primary/90"
            >
              완료
            </button>
          </div>
        </div>
      </ModalWrapper>
    );
  }

  // 결제 처리 중 화면
  if (isProcessing) {
    return (
      <ModalWrapper onClose={() => {}}>
        <div className="flex flex-col items-center justify-center py-12">
          <div className="w-16 h-16 rounded-full border-4 border-primary border-t-transparent animate-spin mb-4" />
          <h2 className="text-xl font-semibold">결제 처리 중...</h2>
          <p className="text-muted-foreground">잠시만 기다려주세요</p>
        </div>
      </ModalWrapper>
    );
  }

  // 결제 입력 화면
  return (
    <ModalWrapper onClose={onClose}>
      <div className="flex flex-col h-full">
        {/* 헤더 */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-xl font-semibold">결제</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-accent"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {/* 결제 금액 */}
          <div className="text-center mb-6">
            <p className="text-sm text-muted-foreground">결제 금액</p>
            <p className="text-3xl font-bold text-primary">
              {formatCurrency(cart.total)}
            </p>
          </div>

          {/* 결제 수단 선택 */}
          <div className="mb-6">
            <p className="text-sm font-medium mb-2">결제 수단</p>
            <div className="grid grid-cols-3 gap-2">
              <PaymentMethodButton
                method="cash"
                icon={<Banknote className="w-5 h-5" />}
                selected={paymentMethod === 'cash'}
                onClick={() => setPaymentMethod('cash')}
              />
              <PaymentMethodButton
                method="card"
                icon={<CreditCard className="w-5 h-5" />}
                selected={paymentMethod === 'card'}
                onClick={() => setPaymentMethod('card')}
              />
              <PaymentMethodButton
                method="transfer"
                icon={<Building2 className="w-5 h-5" />}
                selected={paymentMethod === 'transfer'}
                onClick={() => setPaymentMethod('transfer')}
              />
            </div>
          </div>

          {/* 현금 결제시 금액 입력 */}
          {paymentMethod === 'cash' && (
            <div className="mb-6">
              <p className="text-sm font-medium mb-2">받은 금액</p>
              <div className="text-center mb-4">
                <p className="text-2xl font-bold">{formatCurrency(inputAmount)}</p>
                {change > 0 && (
                  <p className="text-green-500 text-sm mt-1">
                    거스름돈: {formatCurrency(change)}
                  </p>
                )}
                {change < 0 && (
                  <p className="text-red-500 text-sm mt-1">
                    부족: {formatCurrency(Math.abs(change))}
                  </p>
                )}
              </div>
              <AmountPad
                value={inputAmount}
                onChange={setInputAmount}
                quickAmounts={[10000, 50000, 100000]}
              />
            </div>
          )}

          {/* 카드 결제 안내 */}
          {paymentMethod === 'card' && (
            <div className="text-center py-8">
              <CreditCard className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
              <p className="text-lg font-medium mb-2">카드를 단말기에 삽입하세요</p>
              <p className="text-sm text-muted-foreground">
                결제 버튼을 누르면 카드 결제가 진행됩니다
              </p>
            </div>
          )}

          {/* 계좌이체 안내 */}
          {paymentMethod === 'transfer' && (
            <div className="text-center py-8">
              <Building2 className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
              <p className="text-lg font-medium mb-2">계좌이체 정보</p>
              <div className="bg-muted rounded-lg p-4 text-left max-w-sm mx-auto">
                <p className="text-sm text-muted-foreground">입금 계좌</p>
                <p className="font-medium">국민은행 123-456-789012</p>
                <p className="text-sm text-muted-foreground mt-2">예금주</p>
                <p className="font-medium">주식회사 데모</p>
              </div>
            </div>
          )}
        </div>

        {/* 결제 버튼 */}
        <div className="p-4 border-t border-border">
          <button
            onClick={handlePayment}
            disabled={paymentMethod === 'cash' && !isValid}
            className={cn(
              'w-full py-4 rounded-lg font-semibold text-lg transition-colors',
              paymentMethod === 'cash' && !isValid
                ? 'bg-muted text-muted-foreground cursor-not-allowed'
                : 'bg-primary text-primary-foreground hover:bg-primary/90'
            )}
          >
            {paymentMethod === 'cash' ? '현금 결제' : '결제하기'}
          </button>
        </div>
      </div>
    </ModalWrapper>
  );
}

// 모달 래퍼
function ModalWrapper({
  children,
  onClose,
}: {
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* 배경 오버레이 */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />
      {/* 모달 컨텐츠 */}
      <div className="relative bg-card rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-hidden">
        {children}
      </div>
    </div>
  );
}

// 결제 수단 버튼
function PaymentMethodButton({
  method,
  icon,
  selected,
  onClick,
}: {
  method: PaymentMethod;
  icon: React.ReactNode;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-colors',
        selected
          ? 'border-primary bg-primary/10'
          : 'border-border hover:border-primary/50'
      )}
    >
      {icon}
      <span className="text-sm font-medium">{PAYMENT_METHOD_LABELS[method]}</span>
    </button>
  );
}
